import { useState } from 'react';
import { t } from 'i18next';

import { Icon, Loading, SwitchButton } from '../../components/UI';

import { useNavigate } from 'react-router';
import { useAuth } from '../../context/Auth';
import { useSession } from '../../context/Session';
import { useMembership } from '../../context/Membership';

import { trackEvent } from '../../helpers/analytics';

import { CheckoutProps } from './types';

import { FooterNavigator } from '../../components/Footer/FooterNavigator';
import { logger } from '../../helpers/logger';

const Checkout = ({
  membershipPlanId,
  action,
  setIsModalOpen,
}: CheckoutProps) => {
  const { user } = useAuth();
  const {
    membershipPlans,
    membershipPlansLoading,
    initChargebee,
    handleChargebeeCheckout,
    checkoutLoading,
  } = useMembership();

  const navigate = useNavigate();
  const session = useSession();

  initChargebee();

  const [checkoutCompleted, setCheckoutCompleted] = useState<boolean>(false);

  logger.log('Checkout:membershipPlanId', membershipPlanId);

  const membershipPlan = membershipPlans.find((m) => m.id === membershipPlanId);

  const [selectedPrice, setSelectedPrice] = useState(membershipPlan?.prices[0]);

  const onCheckoutComplete = () => {
    trackEvent('subscription_complete', {
      subscription_sku: membershipPlan!.sku,
      subscription_renewal: selectedPrice!.renewal,
      subscription_price: selectedPrice!.price,
      subscription_priceSku: selectedPrice!.sku,
      subscription_id: session.getSessionItem('cbId'),
    });
    setCheckoutCompleted(true);
    navigate('/checkout-success');
  };

  const onCheckoutError = () => {
    navigate('/checkout-error');
  };

  const handleCheckout = () => {
    handleChargebeeCheckout(
      user!.email,
      membershipPlan!.id,
      selectedPrice!.sku,
      onCheckoutComplete,
      onCheckoutError
    );
  };

  return (
    <>
      {membershipPlansLoading ? (
        <Loading className={'mx-auto mt-16'} status={true} />
      ) : (
        <>
          <div className={'flex flex-col w-full h-full overflow-auto'}>
            <div
              className={`w-full ${
                action === 'upgrade' ? 'bg-lime' : 'bg-grey-10'
              } dark:text-black px-8 py-12`}
            >
              <div className={'pb-12 mb-4 text-center'}>
                <div className={'text-2xl uppercase font-semibold mb-2'}>
                  {t(
                    `checkout.${
                      action === 'same'
                        ? 'same'
                        : action === 'upgrade'
                        ? 'upgradeTo'
                        : 'downgradeTo'
                    }`,
                    { plan: membershipPlan?.title }
                  )}
                </div>
                <div className={'text-lg'}>
                  {t('checkout.upgradeToDescription')}
                </div>
              </div>

              <div className={'flex justify-center'}>
                <SwitchButton
                  items={
                    membershipPlan?.prices.map((p) => {
                      return {
                        label: t('checkout.renewal_' + p.renewal),
                        value: p.renewal,
                      };
                    })!
                  }
                  selectedValue={selectedPrice?.renewal || 'MONTHLY'}
                  onClick={(value) => {
                    setSelectedPrice(
                      membershipPlan?.prices.find((p) => p.renewal === value)!
                    );
                  }}
                />
              </div>
              <div className={'text-center text-xl font-semibold mt-8'}>
                {t(`checkout.price`, {
                  price: selectedPrice?.price,
                  renewal: t('checkout.renewal_' + selectedPrice?.renewal),
                })}
              </div>
            </div>

            <div>
              <div
                className={`w-full text-center tex-xl font-semibold mb-8 py-4 bg-black ${
                  action === 'upgrade' ? 'text-lime' : 'text-grey-10'
                }`}
              >
                {t('checkout.includes').toUpperCase()}
              </div>

              <div
                className={
                  'inline-grid grid-cols-2 xl:px-64 lg:px-16 px-8 p-2 w-full dark:text-black'
                }
              >
                {Object.keys(membershipPlan?.features || {})
                  .filter((f) => f !== '__typename')
                  .map((feature) => (
                    <div className={'mb-8'} key={feature}>
                      <div className={'text-center mb-2 '}>
                        {membershipPlan?.features[feature] !== 0 ? (
                          <Icon
                            svgName={'check'}
                            className={`mx-auto w-6 h-6 bg-lime rounded-full p-1`}
                          />
                        ) : (
                          <Icon
                            svgName={'close'}
                            className={
                              'mx-auto w-6 h-6 bg-grey-30 rounded-full p-1 text-black'
                            }
                          />
                        )}
                      </div>

                      <div className={'font-semibold text-lg text-center mb-2'}>
                        {membershipPlan?.features[feature] === true &&
                        feature !== 'swagBag'
                          ? t('checkout.features.label.' + feature)
                          : isNaN(membershipPlan?.features[feature]) &&
                            feature !== 'swagBag'
                          ? t(
                              'checkout.features.value.' +
                                membershipPlan?.features[feature]
                            ) +
                            ' ' +
                            t('checkout.features.label.' + feature)
                          : membershipPlan?.features[feature] === -1 &&
                            feature !== 'swagBag'
                          ? t('checkout.features.value.unlimited') +
                            ' ' +
                            t('checkout.features.label.' + feature)
                          : feature === 'swagBag'
                          ? t('checkout.features.label.' + feature)
                          : feature === 'strategies' ||
                            feature === 'portfolioImport'
                          ? t(
                              'checkout.features.label.' +
                                feature +
                                'WithValue',
                              { [feature]: membershipPlan?.features[feature] }
                            )
                          : membershipPlan?.features[feature] +
                            ' ' +
                            t('checkout.features.label.' + feature)}

                        <br />
                        {feature === 'swagBag' ? (
                          <span
                            className={`text-sm font-normal items-center rounded-lg py-1 px-1 ${
                              action === 'upgrade' ? 'bg-lime' : 'bg-grey-10'
                            }`}
                          >
                            {t('subscriptions.yearlyPlanOnly')}
                          </span>
                        ) : (
                          ''
                        )}
                      </div>
                      <div className={'text-center text-grey-50'}>
                        {t('checkout.features.description.' + feature)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <FooterNavigator
            className={'w-full'}
            isDisabledSaveButton={
              selectedPrice?.sku === user?.membership.sku ||
              (user?.membership?.source === 'MOBILE' && user?.membership?.paid)
            }
            buttonsPosition={'between'}
            backButtonClassName={'border-none'}
            backButtonSVG={<Icon svgName={'arrowBack'} className={'mr-2'} />}
            saveButtonName={
              checkoutCompleted ? t('checkout.close') : t('checkout.continue')
            }
            backButton={true}
            onSave={() => {
              checkoutCompleted ? setIsModalOpen(false) : handleCheckout();
            }}
            onBack={() => {
              setIsModalOpen(false);
            }}
            loading={checkoutLoading}
            oneButton={true}
          />
        </>
      )}
    </>
  );
};
export default Checkout;
